import { useEffect, useState } from 'react'
import {
    Check,
    Download,
    FileText,
    Loader2,
    Pencil,
    Plus,
    Trash2,
    X,
} from 'lucide-react'
import {
    addChunk,
    deleteChunk,
    editChunk,
    getDocumentChunks,
} from '~/api/documents'
import type { DocumentChunksResponse, KnowledgeChunk } from '~/api/types'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { Pagination } from '~/components/ui/pagination'
import { SearchInput } from '~/components/ui/search-input'
import { Skeleton } from '~/components/ui/skeleton'
import { Spinner } from '~/components/ui/spinner'
import { Textarea } from '~/components/ui/textarea'
import { useToast } from '~/components/ui/toast'

// 코퍼스 문서 청크 드릴다운 슬라이드오버. 문서 한 건의 색인 청크를 본문 검색(q)·
// 페이지네이션(limit 20)으로 읽는다(read-only). 청크 본문은 plain text로만 렌더한다.
const PAGE_SIZE = 20

export function KnowledgeDocumentPanel({
    documentId,
    title,
    sourceLabel,
    sourceTone = 'neutral',
    chunkCount,
    onClose,
    onDownload,
}: {
    documentId: string
    title: string
    sourceLabel: string
    sourceTone?: BadgeTone
    chunkCount: number
    onClose: () => void
    // 원본 다운로드(어드민 백엔드 프록시). CLI 적재 코퍼스처럼 documents 행이 없으면 404 → 토스트로 안내.
    onDownload: () => Promise<void>
}) {
    const toast = useToast()
    const [data, setData] = useState<DocumentChunksResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [q, setQ] = useState('')
    const [offset, setOffset] = useState(0)
    const [downloading, setDownloading] = useState(false)
    // 청크 단건 삭제(큐레이션): 확인 대상 + 진행 중 id(버튼 비활성).
    const [pendingDelete, setPendingDelete] = useState<KnowledgeChunk | null>(
        null
    )
    const [deletingId, setDeletingId] = useState<string | null>(null)
    // 청크 인라인 편집(큐레이션): 편집 중 id·초안 본문 + 저장 진행 중 id.
    const [editingId, setEditingId] = useState<string | null>(null)
    const [draft, setDraft] = useState('')
    const [savingId, setSavingId] = useState<string | null>(null)
    // 청크 인라인 추가(큐레이션): 추가 폼 열림 여부·초안 본문 + 저장 진행 중 플래그.
    const [adding, setAdding] = useState(false)
    const [addDraft, setAddDraft] = useState('')
    const [addSaving, setAddSaving] = useState(false)

    const handleDownload = async () => {
        setDownloading(true)
        try {
            await onDownload()
        } finally {
            setDownloading(false)
        }
    }

    // 편집 시작: 해당 청크 본문을 초안으로 싣고 인라인 textarea를 연다.
    const startEdit = (chunk: KnowledgeChunk) => {
        setEditingId(chunk.chunkId)
        setDraft(chunk.text)
    }

    const cancelEdit = () => {
        setEditingId(null)
        setDraft('')
    }

    // 저장: 새 본문을 재임베딩·업서트(editChunk) → 목록의 해당 청크를 낙관적으로 갱신하고 토스트.
    // 재색인하면 원본 기준으로 덮어써지므로, 편집을 지키려면 문서를 수동 관리 잠금해야 한다.
    const saveEdit = async (chunk: KnowledgeChunk) => {
        const text = draft.trim()
        if (!text) {
            toast.error('빈 본문으로 저장할 수 없어요.')
            return
        }
        if (text === chunk.text) {
            cancelEdit()
            return
        }
        setSavingId(chunk.chunkId)
        try {
            const updated = await editChunk(documentId, chunk.chunkId, text)
            setData((prev) =>
                prev
                    ? {
                          ...prev,
                          chunks: prev.chunks.map((c) =>
                              c.chunkId === chunk.chunkId
                                  ? { ...c, text: updated.text }
                                  : c
                          ),
                      }
                    : prev
            )
            cancelEdit()
            toast.success('청크 본문을 수정했어요.')
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setSavingId(null)
        }
    }

    // 추가 시작/취소: 인라인 추가 폼을 열고 닫는다(초안 비움).
    const startAdd = () => {
        setAddDraft('')
        setAdding(true)
    }
    const cancelAdd = () => {
        setAdding(false)
        setAddDraft('')
    }

    // 저장: 새 본문을 임베딩·새 포인트로 업서트(addChunk) → 생성된 청크를 목록 끝에 낙관적으로
    // 붙이고 total을 1 늘린다. order는 서버가 기존 최대 order+1로 잡는다.
    const saveAdd = async () => {
        const text = addDraft.trim()
        if (!text) {
            toast.error('빈 본문으로 추가할 수 없어요.')
            return
        }
        setAddSaving(true)
        try {
            const created = await addChunk(documentId, text)
            setData((prev) =>
                prev
                    ? {
                          ...prev,
                          total: prev.total + 1,
                          chunks: [...prev.chunks, created],
                      }
                    : prev
            )
            cancelAdd()
            toast.success('청크를 코퍼스에 추가했어요.')
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setAddSaving(false)
        }
    }

    // 확인 후 삭제 → Qdrant 포인트 제거(검색·상담 즉시 제외). 성공 시 목록에서 낙관적으로
    // 빼고 total을 줄인다. 재색인하면 원본 기준으로 다시 채워지는 큐레이션 삭제다.
    const confirmDeleteChunk = async () => {
        if (!pendingDelete) return
        const chunk = pendingDelete
        setPendingDelete(null)
        setDeletingId(chunk.chunkId)
        try {
            await deleteChunk(documentId, chunk.chunkId)
            setData((prev) =>
                prev
                    ? {
                          ...prev,
                          total: Math.max(0, prev.total - 1),
                          chunks: prev.chunks.filter(
                              (c) => c.chunkId !== chunk.chunkId
                          ),
                      }
                    : prev
            )
            toast.success('청크를 코퍼스에서 삭제했어요.')
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setDeletingId(null)
        }
    }

    // 검색어가 바뀌면 첫 페이지부터 다시 본다.
    useEffect(() => {
        setOffset(0)
    }, [q])

    // 문서·검색어·페이지가 바뀔 때마다 청크를 다시 조회한다(언마운트 경합 가드).
    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        getDocumentChunks(documentId, { q, limit: PAGE_SIZE, offset })
            .then((d) => {
                if (alive) setData(d)
            })
            .catch((e) => {
                if (alive) setError((e as Error).message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [documentId, q, offset])

    // total은 응답을 우선하되, 첫 로딩 전엔 코퍼스 표의 청크 수로 미리 표시한다.
    const total = data?.total ?? chunkCount
    const chunks = data?.chunks ?? []
    const searching = q.trim().length > 0

    return (
        <div className='fixed inset-0 z-50 flex justify-end'>
            <button
                type='button'
                aria-label='닫기'
                className='bg-background/60 absolute inset-0'
                onClick={onClose}
            />
            <div className='border-border bg-card relative flex h-full w-full max-w-2xl flex-col border-l shadow-lg'>
                <div className='border-border flex items-start justify-between gap-3 border-b p-4'>
                    <div className='min-w-0'>
                        <div className='admin-eyebrow'>코퍼스 문서</div>
                        <h2 className='krds-title text-foreground truncate'>
                            {data?.title ?? title}
                        </h2>
                        <div className='mt-1.5 flex items-center gap-2'>
                            <Badge tone={sourceTone}>{sourceLabel}</Badge>
                            <span className='text-muted-foreground text-[12px]'>
                                총 {total.toLocaleString()}청크
                            </span>
                        </div>
                    </div>
                    <div className='flex shrink-0 items-center gap-1'>
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={handleDownload}
                            disabled={downloading}>
                            {downloading ? (
                                <Loader2 className='animate-spin' />
                            ) : (
                                <Download />
                            )}
                            원본 다운로드
                        </Button>
                        <Button variant='ghost' size='sm' onClick={onClose}>
                            닫기
                        </Button>
                    </div>
                </div>

                <div className='border-border border-b p-4'>
                    <div className='flex items-center gap-2'>
                        <SearchInput
                            className='w-full'
                            value={q}
                            onChange={setQ}
                            placeholder='청크 본문 검색'
                        />
                        {loading ? <Spinner size='sm' /> : null}
                        <Button
                            variant='outline'
                            size='sm'
                            className='shrink-0'
                            onClick={startAdd}
                            disabled={adding}>
                            <Plus />
                            청크 추가
                        </Button>
                    </div>
                    <p className='text-muted-foreground mt-2 text-[11px]'>
                        청크를 추가·편집·삭제하면 검색·상담에 즉시 반영돼요.
                        재색인하면 원본 기준으로 덮어써지니, 수동 편집을
                        지키려면 문서를 수동 관리 잠금하세요.
                    </p>
                    {adding ? (
                        // 인라인 추가 폼: textarea + 추가/취소. 저장은 임베딩·새 포인트 업서트 후 낙관적 추가.
                        <div className='mt-3 space-y-2'>
                            <Textarea
                                value={addDraft}
                                onChange={(e) => setAddDraft(e.target.value)}
                                disabled={addSaving}
                                className='min-h-28 text-[13px]'
                                placeholder='추가할 청크 본문'
                                aria-label='새 청크 본문'
                            />
                            <div className='flex justify-end gap-1'>
                                <Button
                                    variant='ghost'
                                    size='sm'
                                    onClick={cancelAdd}
                                    disabled={addSaving}>
                                    <X />
                                    취소
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={saveAdd}
                                    disabled={addSaving}>
                                    {addSaving ? (
                                        <Loader2 className='animate-spin' />
                                    ) : (
                                        <Check />
                                    )}
                                    추가
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className='flex-1 space-y-2 overflow-y-auto p-4'>
                    {loading && !data ? (
                        Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton key={i} className='h-24' />
                        ))
                    ) : error ? (
                        <p className='text-destructive text-sm'>{error}</p>
                    ) : chunks.length === 0 ? (
                        <EmptyState
                            icon={FileText}
                            title={
                                searching
                                    ? '검색 결과가 없어요.'
                                    : '청크가 없어요.'
                            }
                            description={
                                searching
                                    ? '다른 검색어로 다시 시도해 보세요.'
                                    : '이 문서에 적재된 청크가 없어요.'
                            }
                        />
                    ) : (
                        chunks.map((c) => {
                            // 상단 메타(작은 muted): 위치 경로 → 조항 → 페이지 → 구역 순.
                            const head = c.breadcrumb || c.part
                            const meta = [
                                head,
                                c.clause,
                                c.page != null ? `p.${c.page}` : null,
                                c.area,
                            ].filter(Boolean)
                            const editing = editingId === c.chunkId
                            const saving = savingId === c.chunkId
                            return (
                                <div
                                    key={c.chunkId}
                                    className='group border-border bg-background relative rounded-[var(--radius)] border p-3'>
                                    <div className='text-muted-foreground mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 pr-16 text-[11px]'>
                                        <span className='tnum font-semibold'>
                                            #{c.order}
                                        </span>
                                        {meta.length > 0 ? (
                                            <span className='truncate'>
                                                {meta.join(' · ')}
                                            </span>
                                        ) : null}
                                    </div>
                                    {editing ? (
                                        // 인라인 편집: textarea + 저장/취소. 저장은 재임베딩·업서트 후 낙관적 갱신.
                                        <div className='space-y-2'>
                                            <Textarea
                                                value={draft}
                                                onChange={(e) =>
                                                    setDraft(e.target.value)
                                                }
                                                disabled={saving}
                                                className='min-h-28 text-[13px]'
                                                aria-label='청크 본문 편집'
                                            />
                                            <div className='flex justify-end gap-1'>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={cancelEdit}
                                                    disabled={saving}>
                                                    <X />
                                                    취소
                                                </Button>
                                                <Button
                                                    size='sm'
                                                    onClick={() => saveEdit(c)}
                                                    disabled={saving}>
                                                    {saving ? (
                                                        <Loader2 className='animate-spin' />
                                                    ) : (
                                                        <Check />
                                                    )}
                                                    저장
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p className='text-foreground text-[13px] whitespace-pre-wrap'>
                                                {c.text}
                                            </p>
                                            <div className='absolute top-2 right-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100'>
                                                <Button
                                                    variant='ghost'
                                                    size='icon-sm'
                                                    aria-label='이 청크 편집'
                                                    title='본문 편집'
                                                    disabled={
                                                        deletingId === c.chunkId
                                                    }
                                                    onClick={() => startEdit(c)}
                                                    className='text-muted-foreground hover:text-foreground'>
                                                    <Pencil />
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='icon-sm'
                                                    aria-label='이 청크 삭제'
                                                    title='코퍼스에서 삭제'
                                                    disabled={
                                                        deletingId === c.chunkId
                                                    }
                                                    onClick={() =>
                                                        setPendingDelete(c)
                                                    }
                                                    className='text-muted-foreground hover:text-destructive'>
                                                    {deletingId ===
                                                    c.chunkId ? (
                                                        <Loader2 className='animate-spin' />
                                                    ) : (
                                                        <Trash2 />
                                                    )}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )
                        })
                    )}
                </div>

                {total > PAGE_SIZE ? (
                    <div className='border-border border-t p-3'>
                        <Pagination
                            total={total}
                            pageSize={PAGE_SIZE}
                            offset={offset}
                            onOffset={setOffset}
                            disabled={loading}
                        />
                    </div>
                ) : null}
            </div>

            <ConfirmDialog
                open={pendingDelete !== null}
                title='청크 삭제'
                description='이 청크를 코퍼스에서 삭제할까요? 검색·상담에서 즉시 제외됩니다.'
                confirmLabel='삭제'
                danger
                onConfirm={confirmDeleteChunk}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    )
}
